import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useBreakpointValue } from '@hooks/use-breakpoint-value';
import vars from '@variables';
import { H1Atom } from '@atoms/text-atoms/text-atoms';
import { MastheadProps } from './masthead.types';
import {
  MastheadContainer,
  Banner,
  GatsbyImageWrapper,
  InnerLogo,
  MastheadBelowTitle,
  VideoWrapper,
  AlignableContent,
  BelowTitleContainer,
} from './masthead.styles';
import UtilityBar from '@components/utility-bar/utility-bar';
import { useDataContext } from '@context/page-context-provider';
import { getPageColour } from '../../../../config/cms-mapping-functions';

const Masthead: React.FC<MastheadProps> = ({ hasSecondaryNav }) => {
  const { mastheadData, title } = useDataContext().pageContext;
  const pageColour = getPageColour();

  const {
    videoUrl,
    mastheadBackgroundImage,
    mastheadTabletBgImage,
    mastheadMobileBgImage,
    mastheadAlignContent = 'center',
    mastheadInnerImage,
    mastheadIsFullHeight = false,
    mastheadMoveTitleBelow,
  } = mastheadData!;

  const resolveImage = (image: typeof mastheadBackgroundImage) => {
    if (!image) return null;
    const { contentType, url } = image.file ?? {};
    const isUnsupportedType =
      contentType === 'image/gif' || contentType === 'image/svg+xml';
    return isUnsupportedType ? url : getImage(image!);
  };

  const backgroundImage = useBreakpointValue(
    [
      {
        breakpoint: vars.breakPointFullWidth,
        value: resolveImage(mastheadBackgroundImage),
      },
      {
        breakpoint: vars.breakPointTablet,
        value: resolveImage(mastheadTabletBgImage ?? mastheadBackgroundImage),
      },
      {
        breakpoint: vars.breakPointMinWidth,
        value: resolveImage(mastheadMobileBgImage ?? mastheadBackgroundImage),
      },
    ],
    getImage(mastheadBackgroundImage!)
  );

  const showVideo = !!videoUrl;

  const innerLogo = mastheadInnerImage
    ? resolveImage(mastheadInnerImage)
    : null;

  return (
    <MastheadContainer
      pageColour={pageColour}
      hasSecondaryNav={hasSecondaryNav}
      data-testid="standard-mast"
    >
      <Banner
        data-testid="banner"
        isFullHeight={mastheadIsFullHeight}
        role="banner"
      >
        {showVideo ? (
          <VideoWrapper data-testid="video">
            <video autoPlay loop src={videoUrl} muted playsInline />
          </VideoWrapper>
        ) : (
          backgroundImage && (
            <GatsbyImageWrapper data-testid="mast-bg-image">
              {typeof backgroundImage === 'string' ? (
                <div>
                  <img
                    src={backgroundImage}
                    alt={mastheadBackgroundImage?.description ?? ''}
                    loading="eager"
                  />
                </div>
              ) : (
                <GatsbyImage
                  image={backgroundImage}
                  alt={mastheadBackgroundImage?.description ?? ''}
                  loading="eager"
                />
              )}
            </GatsbyImageWrapper>
          )
        )}
        <AlignableContent
          data-testid="mast-content-wrapper"
          align={mastheadAlignContent}
          pageColour={pageColour}
        >
          {innerLogo && (
            <InnerLogo data-testid="inner-logo">
              {typeof innerLogo === 'string' ? (
                <div>
                  <img
                    src={innerLogo}
                    alt={mastheadInnerImage?.description ?? ''}
                    loading="eager"
                  />
                </div>
              ) : (
                <GatsbyImage
                  image={innerLogo}
                  alt={mastheadInnerImage?.description ?? ''}
                  loading="eager"
                />
              )}
            </InnerLogo>
          )}
          {!mastheadMoveTitleBelow && !!title && (
            <H1Atom data-testid="mast-heading">
              <span>{title}</span>
            </H1Atom>
          )}
        </AlignableContent>
      </Banner>
      <UtilityBar />
      {mastheadMoveTitleBelow && !!title && (
        <BelowTitleContainer>
          <MastheadBelowTitle data-testid="mast-heading-below">
            {title}
          </MastheadBelowTitle>
        </BelowTitleContainer>
      )}
    </MastheadContainer>
  );
};

export default Masthead;
